// Progress & activity (PLAN §12.5): one event model for every automated process, streamed per user.

export type JobKind =
  | 'ingest'
  | 'dataset_import'
  | 'recheck'
  | 'merge'
  | 'report'
  | 'scheduled_run'
  | 'cluster'
  | 'simulation'
  | 'scrape'
  | 'social_feed'
  | 'register_fetch';
export type JobStatus = 'queued' | 'running' | 'awaiting_input' | 'paused' | 'done' | 'failed' | 'cancelled';

export interface StageDef {
  key: string;
  label: string;
  /** No heartbeat for longer than this turns the card amber ("Taking longer than usual"). */
  stallAfterMs: number;
}

/** Ingest stepper. Library-only files finish at `indexed`. */
export const INGEST_STAGES: StageDef[] = [
  { key: 'uploading', label: 'Uploading', stallAfterMs: 30_000 },
  { key: 'reading', label: 'Reading', stallAfterMs: 60_000 },
  { key: 'ocr', label: 'OCR / Transcribing', stallAfterMs: 90_000 },
  { key: 'indexed', label: 'Indexed in Library', stallAfterMs: 60_000 },
  { key: 'extracting', label: 'Extracting', stallAfterMs: 180_000 },
  { key: 'matching', label: 'Matching', stallAfterMs: 120_000 },
  { key: 'ready', label: 'Ready for review', stallAfterMs: Number.POSITIVE_INFINITY },
];

/** The upload card (task 10): sending the parts, then content and marking checks. Processing continues on the ingest stepper. */
export const UPLOAD_STAGES: StageDef[] = [
  { key: 'uploading', label: 'Uploading', stallAfterMs: 30_000 },
  { key: 'checking', label: 'Checking the file', stallAfterMs: 60_000 },
];

/** Merge and unmerge (task 22): quick, one batch; the card records that it happened and links to the survivor. */
export const MERGE_STAGES: StageDef[] = [
  { key: 'moving', label: 'Moving claims, links and names', stallAfterMs: 60_000 },
  { key: 'done', label: 'Done', stallAfterMs: Number.POSITIVE_INFINITY },
];

export interface JobCounters {
  actors?: number;
  relations?: number;
  events?: number;
  [k: string]: number | undefined;
}

/**
 * One route of a Compare-mode run (PLAN §7.1: "one lane per compared route"): its own stage, counters, time and
 * cost. A failed lane is shown as failed; the other lanes carry on.
 */
export interface JobLane {
  key: string;
  label: string;
  status: 'running' | 'done' | 'failed';
  /** Stage label within the lane ("Extracting", "Matching", "Ready"). */
  stage: string;
  /** 0–100 within the lane. */
  percent: number;
  counters?: JobCounters;
  costUsd: number;
  elapsedMs: number;
  error?: string;
}

export interface JobProgress {
  id: string;
  kind: JobKind;
  /** Generic title, never confidential content ("Upload: 3 files", "sheet.docx"). */
  title: string;
  stages: StageDef[];
  stageIndex: number;
  /** "OCR page 3 of 12", "Transcribing 04:10 / 18:30". */
  subStep?: string;
  /** 0–100 overall. */
  percent: number;
  counters?: JobCounters;
  status: JobStatus;
  startedAt: number;
  /** Heartbeat: time of the last event from the worker. */
  updatedAt: number;
  etaMs?: number;
  /** Plain-language reason when failed. */
  error?: { reason: string; retryable: boolean };
  /** Where the card links (source page, review inbox…). */
  link?: string;
  /** Compare mode: one lane per compared route. */
  lanes?: JobLane[];
}

export type ActivityMessage =
  | { type: 'snapshot'; jobs: JobProgress[]; serverTime: number }
  | { type: 'job'; job: JobProgress; serverTime: number }
  | { type: 'notification'; notification: NotificationItem }
  | { type: 'pong'; serverTime: number };

export interface NotificationItem {
  id: string;
  type: 'job_done' | 'job_failed' | 'review_ready' | 'input_needed' | 'mention' | 'admin' | 'alert';
  /** Generic text only: notifications never contain confidential data. */
  title: string;
  link?: string;
  createdAt: number;
  readAt?: number | null;
}

export type Liveness = 'live' | 'stalled' | 'failed' | 'finished';

/** Amber after the stage's threshold without a heartbeat; red on failure. */
export function jobLiveness(job: JobProgress, now: number): Liveness {
  if (job.status === 'failed') return 'failed';
  if (job.status === 'done' || job.status === 'cancelled') return 'finished';
  if (job.status === 'paused' || job.status === 'awaiting_input') return 'live';
  const stage = job.stages[job.stageIndex];
  return stage && now - job.updatedAt > stage.stallAfterMs ? 'stalled' : 'live';
}

export const isActive = (j: JobProgress) =>
  j.status === 'queued' || j.status === 'running' || j.status === 'awaiting_input' || j.status === 'paused';

/** "4s", "3 min", "1 h 5 min". */
export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}
